import httpClient from './httpClient';
import { Paciente, PacientesPaginatedResponse } from '../types';

// Función para refrescar token
const refreshToken = async (): Promise<string | null> => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return null;
    }

    const response = await httpClient.post('/api/auth/refresh', { refreshToken });
    const { token } = response.data;
    localStorage.setItem('token', token);
    return token;
  } catch (error) {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    return null;
  }
};

// Obtener pacientes
export const fetchPacientes = async (params?: any): Promise<Paciente[] | PacientesPaginatedResponse> => {
  try {
    // Verificar que tenemos un token
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No se encontró el token de autenticación');
    }

    let url = '/api/pacientes';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key].toString());
        }
      });
      url += `?${queryParams.toString()}`;
    }

    const response = await httpClient.get(url);
    return response.data;

  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Intentar refrescar el token
      const newToken = await refreshToken();
      if (!newToken) {
        // Si no se puede refrescar, redirigir al login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      }
      // Reintentar con el nuevo token
      const retryResponse = await httpClient.get('/api/pacientes');
      return retryResponse.data;
    }
    throw error;
  }
};

// Crear un nuevo paciente
export const createPaciente = async (paciente: Omit<Paciente, 'codigo'>): Promise<Paciente> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No se encontró el token de autenticación');
    }

    const response = await httpClient.post('/api/pacientes', paciente);
    return response.data;

  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Intentar refrescar el token
      const newToken = await refreshToken();
      if (!newToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      }
      // Reintentar con el nuevo token
      const retryResponse = await httpClient.post('/api/pacientes', paciente);
      return retryResponse.data;
    }

    // Manejo específico para errores de código duplicado
    if (error.response?.data?.message?.includes('duplicate key error') ||
      error.response?.data?.message?.includes('Ya existe un paciente con este código')) {
      throw new Error('El código de paciente ya existe. Por favor, intente nuevamente.');
    }

    throw error;
  }
};

// Actualizar un paciente existente
export const updatePaciente = async (codigo: string, paciente: Partial<Paciente>): Promise<Paciente> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No se encontró el token de autenticación');
    }

    const response = await httpClient.put(`/api/pacientes/${codigo}`, paciente);
    return response.data;

  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Intentar refrescar el token
      const newToken = await refreshToken();
      if (!newToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      }
      // Reintentar con el nuevo token
      const retryResponse = await httpClient.put(`/api/pacientes/${codigo}`, paciente);
      return retryResponse.data;
    }

    // Manejo específico para paciente no encontrado
    if (error.response?.status === 404) {
      throw new Error('Paciente no encontrado');
    }

    throw error;
  }
};

// Eliminar paciente
export const deletePaciente = async (codigo: string): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No se encontró el token de autenticación');
    }

    await httpClient.delete(`/api/pacientes/${codigo}`);
    return; // Éxito

  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Intentar refrescar el token
      const newToken = await refreshToken();
      if (!newToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      }
      // Reintentar con el nuevo token
      await httpClient.delete(`/api/pacientes/${codigo}`);
      return;
    }

    // Manejo específico para paciente no encontrado
    if (error.response?.status === 404) {
      throw new Error('El paciente no existe o ya fue eliminado');
    }

    throw error;
  }
};